// The hotel's property switcher (board 27 properties--consolidated, the open
// chip popover): «عقاراتي», each property by its branch name over «المدينة ·
// عدد الغرف» (the city from the branch, the room count from erp-core's
// properties read — the city alone while that read is missing), the current one
// checked; the owner also gets «كل العقارات — عرض مجمّع للمالك» (opens
// /hotel/properties) and «إضافة عقار» (opens the branch editor on a new
// branch). The nouns are terminology's (`properties_mine` · `properties_all` ·
// `property_indef`). Mounted by the business chip for a hotel with ≥ 2
// properties; the clinic keeps its «الفرع» list.

import { Building, Building2, Check, Layers, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useErp } from '@/app/erp-context';
import { usePropertiesQuery } from '../data/properties';
import { PROPERTIES_COPY, switcherSub } from '../lib/properties';
import { HOTEL_PATHS } from '../menu';

export interface SwitcherBranch {
  id: string;
  name: string;
  is_main: boolean;
  city?: string;
}

export interface PropertySwitcherProps {
  branches: readonly SwitcherBranch[];
  activeBranchId: string | null;
  /** The main branch is the server default — the chip passes `null` for it, as for the plain list. */
  onPick: (b: SwitcherBranch) => void;
  onClose: () => void;
}

export function PropertySwitcher({ branches, activeBranchId, onPick, onClose }: PropertySwitcherProps) {
  const { me, t, flag } = useErp();
  const navigate = useNavigate();
  const owner = me.role === 'owner';
  // the room-count read is the owner's and the property manager's: reception gets 403 there
  const counts = usePropertiesQuery({ enabled: flag('erp.hotel') && (owner || me.role === 'manager') });
  const rooms = new Map((counts.data?.properties ?? []).map((p) => [p.branch_id, p]));

  const go = (to: string) => {
    onClose();
    navigate(to);
  };

  return (
    <>
      <div className="e-popover-label">{t('properties_mine')}</div>
      <div role="listbox" aria-label={t('property')} data-testid="branch-switcher">
        {branches.map((b) => {
          const row = rooms.get(b.id);
          const active = b.id === activeBranchId;
          const Icon = active ? Building2 : Building;
          return (
            <button key={b.id} type="button" role="option" aria-selected={active} className="e-popover-item e-popover-item--property" onClick={() => onPick(b)}>
              <Icon aria-hidden="true" className="e-popover-item-icon" />
              <span className="e-popover-item-text">
                <span className="e-popover-item-title">{b.name}</span>
                <span className="e-popover-item-sub">{switcherSub(row?.city ?? b.city ?? '', row?.rooms)}</span>
              </span>
              {active && <Check aria-hidden="true" width={16} height={16} />}
            </button>
          );
        })}
      </div>
      {owner && (
        <>
          <button type="button" className="e-popover-item e-popover-item--property" onClick={() => go(HOTEL_PATHS.properties)} data-testid="switcher-all">
            <Layers aria-hidden="true" className="e-popover-item-icon" />
            <span className="e-popover-item-text">
              <span className="e-popover-item-title">{t('properties_all')}</span>
              <span className="e-popover-item-sub">{PROPERTIES_COPY.allHint}</span>
            </span>
          </button>
          <button type="button" className="e-popover-add" onClick={() => go('/settings/branches?new=1')} data-testid="switcher-add">
            <Plus aria-hidden="true" width={16} height={16} />
            {`إضافة ${t('property_indef')}`}
          </button>
        </>
      )}
    </>
  );
}
